import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/Dialog';
import { Projekt, Zuweisung, Mitarbeiter, ProjektStatus } from '../types';
import { Building2, Calendar, Users, Activity, CheckCircle2, PauseCircle, PlayCircle } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './ui/Button';
interface ProjectDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projekt: Projekt | null;
  zuweisungen: Zuweisung[];
  mitarbeiter: Mitarbeiter[];
  onStatusChange: (projektId: string, status: ProjektStatus) => void;
}
export function ProjectDetailDialog({
  open,
  onOpenChange,
  projekt,
  zuweisungen,
  mitarbeiter,
  onStatusChange
}: ProjectDetailDialogProps) {
  if (!projekt) return null;
  // Find assignments for this project
  const projectAssignments = zuweisungen.filter(z => z.projektId === projekt.id);
  // Get unique team members
  const teamMembers = Array.from(new Set(projectAssignments.map(z => z.mitarbeiterId))).map(id => mitarbeiter.find(m => m.id === id)).filter((m): m is Mitarbeiter => !!m);
  return <Dialog open={open} onOpenChange={onOpenChange} data-id="element-278">
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden gap-0 border-none shadow-2xl" data-id="element-279">
        {/* Header with Color Banner */}
        <div className="relative" data-id="element-280">
          <div className="h-24 w-full" style={{
          backgroundColor: projekt.farbe
        }} data-id="element-281" />
          <div className="absolute -bottom-12 left-6" data-id="element-282">
            <div className="h-24 w-24 rounded-xl bg-background border-4 border-background shadow-lg flex items-center justify-center" data-id="element-283">
              <div className="text-3xl font-bold" style={{
              color: projekt.farbe
            }} data-id="element-284">
                {projekt.projektnummer.split('-')[1] || 'P'}
              </div>
            </div>
          </div>
          <div className="absolute bottom-4 right-6 flex gap-2" data-id="element-285">
            <div className="bg-black/20 backdrop-blur-md text-white px-3 py-1 rounded-full text-xs font-bold shadow-sm border border-white/10" data-id="element-286">
              {projekt.status}
            </div>
          </div>
        </div>

        <div className="pt-16 px-6 pb-6 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-287">
          {/* Title Section */}
          <div data-id="element-288">
            <h2 className="text-2xl font-bold tracking-tight" data-id="element-289">
              {projekt.name}
            </h2>
            <div className="flex items-center gap-2 text-muted-foreground font-medium mt-1" data-id="element-290">
              <Building2 className="h-4 w-4" data-id="element-291" />
              {projekt.kunde}
              <span className="text-border mx-1" data-id="element-292">•</span>
              <span className="text-foreground" data-id="element-293">{projekt.projektnummer}</span>
            </div>
          </div>

          {/* Status Actions */}
          <div className="flex gap-2 p-1 bg-muted/20 rounded-lg border" data-id="element-294">
            {(['Aktiv', 'Pausiert', 'Abgeschlossen'] as ProjektStatus[]).map(status => <button key={status} onClick={() => onStatusChange(projekt.id, status)} className={cn('flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2', projekt.status === status ? 'bg-background shadow-sm text-foreground ring-1 ring-border' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground')} data-id="element-295">
                  {status === 'Aktiv' && <PlayCircle className="h-4 w-4 text-green-500" data-id="element-296" />}
                  {status === 'Pausiert' && <PauseCircle className="h-4 w-4 text-orange-500" data-id="element-297" />}
                  {status === 'Abgeschlossen' && <CheckCircle2 className="h-4 w-4 text-blue-500" data-id="element-298" />}
                  {status}
                </button>)}
          </div>

          <div className="grid grid-cols-2 gap-6" data-id="element-299">
            <div className="space-y-1" data-id="element-300">
              <div className="text-xs font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-301">
                <Calendar className="h-3.5 w-3.5" data-id="element-302" />
                Zeitraum
              </div>
              <div className="font-medium text-lg" data-id="element-303">
                KW {projekt.startKw} - {projekt.endKw}
              </div>
            </div>
            <div className="space-y-1" data-id="element-304">
              <div className="text-xs font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-305">
                <Activity className="h-3.5 w-3.5" data-id="element-306" />
                Aktuelle Phase
              </div>
              <div className="font-medium text-lg" data-id="element-307">{projekt.phase}</div>
            </div>
          </div>

          <div className="h-px bg-border" data-id="element-308" />

          {/* Team Section */}
          <div className="space-y-4" data-id="element-309">
            <div className="flex items-center justify-between" data-id="element-310">
              <h3 className="text-sm font-bold uppercase text-muted-foreground tracking-wider flex items-center gap-2" data-id="element-311">
                <Users className="h-4 w-4" data-id="element-312" />
                Projektteam ({teamMembers.length})
              </h3>
            </div>

            <div className="space-y-3" data-id="element-313">
              {teamMembers.map(member => {
              const memberAssignments = projectAssignments.filter(z => z.mitarbeiterId === member.id);
              return <div key={member.id} className="p-3 rounded-lg border bg-card shadow-sm flex items-center gap-4" data-id="element-314">
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center font-bold text-muted-foreground text-xs border" data-id="element-315">
                      {member.name.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-316">
                      <div className="font-bold text-sm truncate" data-id="element-317">
                        {member.name}
                      </div>
                      <div className="text-xs text-muted-foreground" data-id="element-318">
                        {member.abteilung}
                      </div>
                    </div>
                    <div className="text-right" data-id="element-319">
                      {memberAssignments.map(z => <div key={z.id} className="text-xs font-medium bg-muted px-2 py-0.5 rounded mb-1 last:mb-0" data-id="element-320">
                          {z.phase} ({z.auslastungProzent}%)
                        </div>)}
                    </div>
                  </div>;
            })}

              {teamMembers.length === 0 && <div className="text-sm text-muted-foreground italic py-2" data-id="element-321">
                  Noch keine Mitarbeiter zugewiesen.
                </div>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
}